import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { uploadPhoto } from "./uploadPhoto";
import NavBar from "./NavBar";

const API_BASE_URL = "https://api.itrustsecure.com";
function getToken() { return localStorage.getItem("itrust_token"); }
function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

const STATUS_STEPS = [
  { key: "OPEN", label: "Dispute Filed", icon: "📋", desc: "Your dispute has been received" },
  { key: "UNDER_REVIEW", label: "Under Review", icon: "🔍", desc: "Admin is reviewing the evidence" },
  { key: "RESOLVED", label: "Resolved", icon: "✅", desc: "A decision has been made" },
];

export default function DisputeTrackPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingEvidence, setAddingEvidence] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newPhotos, setNewPhotos] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/orders/${orderId}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { if (d.id) setOrder(d); })
      .finally(() => setLoading(false));
  }, [orderId]);

  async function handlePhotoUpload(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = await Promise.all(files.map(f => uploadPhoto(f)));
      setNewPhotos(prev => [...prev, ...urls]);
    } catch { window.alert("Photo upload failed."); }
    finally { setUploading(false); e.target.value = ""; }
  }

  async function handleAddEvidence() {
    if (newPhotos.length === 0 && !newNote.trim()) {
      window.alert("Please add a photo or note"); return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/disputes/${order.dispute.id}/evidence`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ photoUrls: newPhotos, note: newNote.trim() || null })
      });
      if (res.ok) {
        setNewPhotos([]); setNewNote(""); setAddingEvidence(false);
        const updated = await fetch(`${API_BASE_URL}/orders/${orderId}`, { headers: authHeaders() });
        const d = await updated.json();
        if (d.id) setOrder(d);
      } else {
        const d = await res.json(); window.alert(d.error || "Could not add evidence");
      }
    } catch { window.alert("Something went wrong"); }
    finally { setSubmitting(false); }
  }

  const s = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", color: "#0f172a", minHeight: "100vh" },
    main: { maxWidth: 680, margin: "0 auto", padding: "24px 20px" },
    backBtn: { background: "transparent", border: "none", color: "#64748b", cursor: "pointer", fontSize: 14, marginBottom: 20, padding: 0 },
    card: { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 16, padding: 20, marginBottom: 14, backdropFilter: "blur(8px)", boxShadow: "0 2px 12px rgba(59,130,246,0.08)" },
    sectionTitle: { fontSize: 13, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 14 },
    statusBadge: (status) => {
      const colors = { OPEN: "#d97706", UNDER_REVIEW: "#3b82f6", RESOLVED: "#16a34a", CLOSED: "#6b7280" };
      const c = colors[status] || "#6b7280";
      return { display: "inline-block", padding: "5px 14px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: `${c}15`, color: c, border: `1px solid ${c}30` };
    },
    timelineStep: (done, current) => ({ display: "flex", gap: 14, alignItems: "flex-start", opacity: done || current ? 1 : 0.35 }),
    timelineIcon: (done, current) => ({ width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0, background: done ? "#3b82f6" : current ? "rgba(59,130,246,0.12)" : "rgba(100,116,139,0.08)", border: done ? "2px solid #3b82f6" : current ? "2px solid rgba(59,130,246,0.35)" : "2px solid rgba(100,116,139,0.15)" }),
    timelineLine: (done) => ({ width: 2, height: 20, background: done ? "#3b82f6" : "rgba(100,116,139,0.12)", margin: "0 17px" }),
    evidenceImg: { width: 90, height: 90, objectFit: "cover", borderRadius: 10, border: "1px solid rgba(59,130,246,0.15)" },
    addBtn: { background: "rgba(59,130,246,0.06)", color: "#3b82f6", border: "1px solid rgba(59,130,246,0.2)", padding: "12px 20px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 14, width: "100%" },
    uploadBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px", background: "rgba(59,130,246,0.06)", border: "1px dashed rgba(59,130,246,0.3)", borderRadius: 10, color: "#3b82f6", cursor: "pointer", fontWeight: 700, fontSize: 13, marginBottom: 10, width: "100%", boxSizing: "border-box" },
    input: { width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.8)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 14, marginBottom: 10, boxSizing: "border-box", outline: "none", minHeight: 80, resize: "vertical" },
    submitBtn: { background: "#3b82f6", color: "#fff", border: "none", padding: "12px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, width: "100%" },
    cancelBtn: { background: "transparent", border: "1px solid rgba(59,130,246,0.2)", color: "#64748b", padding: "12px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, width: "100%", marginBottom: 8 },
  };

  if (loading) return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh", color: "#94a3b8" }}>Loading...</div>
    </div>
  );

  if (!order?.dispute) return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh", color: "#94a3b8" }}>No dispute found for this order</div>
    </div>
  );

  const dispute = order.dispute;
  const currentStep = STATUS_STEPS.findIndex(s => s.key === dispute.status);
  const isResolved = ["RESOLVED", "CLOSED"].includes(dispute.status);
  const isBuyer = order.buyerId === user?.id;

  return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={s.main}>
        <button style={s.backBtn} onClick={() => navigate(`/orders/${orderId}`)}>← Back to Order</button>

        {/* Header */}
        <div style={s.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", marginBottom: 4 }}>Dispute</div>
              <div style={{ fontSize: 13, color: "#64748b" }}>Order #{orderId?.slice(0, 8)}</div>
            </div>
            <span style={s.statusBadge(dispute.status)}>{dispute.status.replace(/_/g, " ")}</span>
          </div>
          {dispute.reason && (
            <div style={{ fontSize: 14, color: "#475569", padding: "12px 14px", background: "rgba(59,130,246,0.04)", borderRadius: 10, marginTop: 8 }}>
              "{dispute.reason}"
            </div>
          )}
          {dispute.description && (
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 10, lineHeight: 1.6 }}>{dispute.description}</div>
          )}
        </div>

        {/* Timeline */}
        <div style={s.card}>
          <div style={s.sectionTitle}>Status</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {STATUS_STEPS.map((step, i) => {
              const done = currentStep > i;
              const current = currentStep === i;
              const isLast = i === STATUS_STEPS.length - 1;
              return (
                <div key={step.key}>
                  <div style={s.timelineStep(done, current)}>
                    <div style={s.timelineIcon(done, current)}>
                      {done ? "✓" : step.icon}
                    </div>
                    <div style={{ paddingTop: 6 }}>
                      <div style={{ fontSize: 14, fontWeight: current ? 700 : 600, color: done || current ? "#1e293b" : "#94a3b8" }}>{step.label}</div>
                      {current && <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{step.desc}</div>}
                    </div>
                  </div>
                  {!isLast && <div style={s.timelineLine(done)} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Admin decision */}
        {isResolved && dispute.resolution && (
          <div style={{ ...s.card, borderColor: "rgba(22,163,74,0.25)", background: "rgba(22,163,74,0.04)" }}>
            <div style={s.sectionTitle}>Admin Decision</div>
            <div style={{ fontSize: 14, color: "#1e293b", lineHeight: 1.6 }}>{dispute.resolution}</div>
          </div>
        )}

        {/* Evidence submitted */}
        {dispute.evidence?.length > 0 && (
          <div style={s.card}>
            <div style={s.sectionTitle}>Evidence Submitted</div>
            {dispute.evidence.map((ev, i) => (
              <div key={ev.id || i} style={{ paddingBottom: 14, marginBottom: 14, borderBottom: i < dispute.evidence.length - 1 ? "1px solid rgba(59,130,246,0.08)" : "none" }}>
                <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8 }}>
                  Submitted {new Date(ev.createdAt).toLocaleDateString()}
                  {ev.submittedBy?.fullName && ` · ${ev.submittedBy.fullName}`}
                </div>
                {ev.note && <div style={{ fontSize: 14, color: "#475569", marginBottom: 8, lineHeight: 1.5 }}>{ev.note}</div>}
                {ev.photoUrls?.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {ev.photoUrls.map((url, pi) => (
                      <img key={pi} src={url} alt={`Evidence ${pi + 1}`} style={s.evidenceImg} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add more evidence — only if dispute still open */}
        {!isResolved && isBuyer && (
          <div style={s.card}>
            <div style={s.sectionTitle}>Add More Evidence</div>
            {!addingEvidence ? (
              <button style={s.addBtn} onClick={() => setAddingEvidence(true)}>
                + Add Photos or Note
              </button>
            ) : (
              <>
                {newPhotos.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                    {newPhotos.map((url, i) => (
                      <div key={i} style={{ position: "relative" }}>
                        <img src={url} alt="" style={s.evidenceImg} />
                        <button
                          onClick={() => setNewPhotos(prev => prev.filter((_, pi) => pi !== i))}
                          style={{ position: "absolute", top: -6, right: -6, background: "#ef4444", border: "none", borderRadius: "50%", width: 20, height: 20, color: "#fff", cursor: "pointer", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}
                        >✕</button>
                      </div>
                    ))}
                  </div>
                )}
                {uploading ? (
                  <div style={{ ...s.uploadBtn, opacity: 0.6 }}>⏳ Uploading...</div>
                ) : (
                  <>
                    <div style={s.uploadBtn} onClick={() => fileRef.current?.click()}>📷 Add Photos</div>
                    <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handlePhotoUpload} />
                  </>
                )}
                <textarea
                  style={s.input}
                  placeholder="Add a note to support your case..."
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                />
                <button style={s.cancelBtn} onClick={() => { setAddingEvidence(false); setNewPhotos([]); setNewNote(""); }}>
                  Cancel
                </button>
                <button
                  style={{ ...s.submitBtn, opacity: submitting ? 0.5 : 1 }}
                  onClick={handleAddEvidence}
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "Submit Evidence"}
                </button>
              </>
            )}
          </div>
        )}

        {/* What happens next */}
        {!isResolved && (
          <div style={{ background: "rgba(59,130,246,0.04)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#3b82f6", marginBottom: 6 }}>What happens next?</div>
            <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
              Our team will review all evidence and contact both parties within 48 hours. The seller has been notified and may submit a response. You'll be notified when a decision is made.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}